import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './EnterpriseAuthContext';

/**
 * Background AI jobs (ai_requests) for the signed-in user.
 *
 * One scheduler owns all refreshing:
 * - a single realtime channel filtered by user_id (when the table is in the realtime
 *   publication it makes updates near-instant);
 * - one fallback poll, which is the real mechanism while ai_requests is not in the realtime
 *   publication: every 3s while jobs run (backing off x1.5 to at most 5s while nothing changes,
 *   so a finished job still shows within seconds), and every 45s when idle (to catch jobs
 *   started by edge functions); paused while the tab is hidden and run immediately when it
 *   becomes visible again.
 * State is only replaced when the rows actually changed, and the context value is memoized,
 * so polls never re-render the app when nothing happened.
 *
 * `completedLoaded` turns true once the first completed-jobs fetch for the signed-in user has
 * resolved, so watchers can tell the sign-in history apart from jobs that finish later.
 */

const AIRequestContext = createContext();

const ACTIVE_STATUSES = ['pending', 'processing'];
const ACTIVE_POLL_MS = 3000;
const ACTIVE_POLL_MAX_MS = 5000;
const IDLE_POLL_MS = 45000;
/** Refresh the completed list every Nth idle tick (in case a job finished between polls). */
const IDLE_COMPLETED_EVERY = 4;
const REALTIME_DEBOUNCE_MS = 250;

/**
 * Change signature of a request list (ids + the fields the UI shows).
 * @param {Array<object>} rows - ai_requests rows.
 * @returns {string} Signature.
 */
export function requestsSignature(rows) {
  return (rows || [])
    .map((r) => [r.id, r.status, r.updated_at || '', r.progress ?? '', r.progress_message || '', r.dismissed ? 1 : 0, r.completed_at || ''].join(':'))
    .join('|');
}

/**
 * Next poll delay.
 * @param {{activeCount: number, changed: boolean, previousDelay: number}} state - Poll state.
 * @returns {number} Delay in ms.
 */
export function nextPollDelay({ activeCount, changed, previousDelay }) {
  if (!activeCount) return IDLE_POLL_MS;
  if (changed || !previousDelay || previousDelay >= IDLE_POLL_MS) return ACTIVE_POLL_MS;
  return Math.min(ACTIVE_POLL_MAX_MS, Math.round(previousDelay * 1.5));
}

/**
 * Ids in `rows` that are not in `seen`.
 * @param {Array<{id: string}>} rows - Current rows.
 * @param {Set<string>} seen - Ids already known.
 * @returns {string[]} New ids.
 */
export function newRequestIds(rows, seen) {
  return (rows || []).map((r) => r.id).filter((id) => !seen.has(id));
}

export const useAIRequests = () => {
  const context = useContext(AIRequestContext);
  if (!context) {
    throw new Error('useAIRequests must be used within AIRequestProvider');
  }
  return context;
};

export const AIRequestProvider = ({ children }) => {
  const { state: authState } = useAuth();
  const userId = authState.user?.id || null;
  const [activeRequests, setActiveRequests] = useState([]);
  const [completedRequests, setCompletedRequests] = useState([]);
  /** User id whose first completed-jobs fetch has resolved (null until then). */
  const [completedLoadedFor, setCompletedLoadedFor] = useState(null);

  const activeSigRef = useRef('');
  const completedSigRef = useRef('');
  const activeIdsRef = useRef(new Set());
  const timerRef = useRef(null);
  const delayRef = useRef(0);
  const idleTicksRef = useRef(0);
  const inFlightRef = useRef(null);
  /** Refresh sequence: a refresh's result is dropped when a newer one was already applied. */
  const refreshSeqRef = useRef(0);
  const appliedActiveSeqRef = useRef(0);
  const appliedCompletedSeqRef = useRef(0);
  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  /**
   * Replace a list only when its signature changed.
   * @param {'active'|'completed'} which - List.
   * @param {Array<object>} rows - Rows.
   * @returns {boolean} Whether it changed.
   */
  const commit = useCallback((which, rows) => {
    const sig = requestsSignature(rows);
    const ref = which === 'active' ? activeSigRef : completedSigRef;
    if (sig === ref.current) return false;
    ref.current = sig;
    (which === 'active' ? setActiveRequests : setCompletedRequests)(rows);
    return true;
  }, []);

  /**
   * Fetch the active (pending/processing) rows and apply them unless a newer refresh already
   * applied its result, so overlapping refreshes never roll the list back.
   * @param {number} seq - Refresh sequence number.
   * @returns {Promise<{rows: Array|null, changed: boolean}>} Rows (null when skipped or failed).
   */
  const fetchActiveRequests = useCallback(async (seq) => {
    const uid = userIdRef.current;
    if (!uid) return { rows: null, changed: false };
    try {
      const { data, error } = await supabase
        .from('ai_requests')
        .select('*')
        .eq('user_id', uid)
        .in('status', ACTIVE_STATUSES)
        .order('created_at', { ascending: false });
      if (error) throw error;
      if (userIdRef.current !== uid || seq < appliedActiveSeqRef.current) return { rows: null, changed: false };
      appliedActiveSeqRef.current = seq;
      const rows = data || [];
      const changed = commit('active', rows);
      return { rows, changed };
    } catch (error) {
      console.error('Error fetching active requests:', error);
      return { rows: null, changed: false };
    }
  }, [commit]);

  /**
   * Fetch recently finished rows (last 24h, not dismissed). The first attempt for a user marks
   * the completed list as loaded, even when it failed, so watchers never wait forever.
   * @param {number} seq - Refresh sequence number.
   * @returns {Promise<void>}
   */
  const fetchCompletedRequests = useCallback(async (seq) => {
    const uid = userIdRef.current;
    if (!uid) return;
    try {
      const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .from('ai_requests')
        .select('*')
        .eq('user_id', uid)
        .in('status', ['completed', 'failed'])
        .or('dismissed.eq.false,dismissed.is.null')
        .gte('completed_at', oneDayAgo)
        .order('completed_at', { ascending: false })
        .limit(10);
      if (error) throw error;
      if (userIdRef.current !== uid || seq < appliedCompletedSeqRef.current) return;
      appliedCompletedSeqRef.current = seq;
      commit('completed', data || []);
    } catch (error) {
      console.error('Error fetching completed requests:', error);
    } finally {
      if (userIdRef.current === uid) setCompletedLoadedFor(uid);
    }
  }, [commit]);

  /**
   * One refresh: active list, plus the completed list when a job left the active set (or when
   * asked). Timer polls share an in-flight refresh; `force` (used after a write such as create
   * or cancel) always starts a fresh one so the result includes that write.
   * @param {{completed?: boolean, force?: boolean}} [options] - Also refresh completed requests; bypass the in-flight refresh.
   * @returns {Promise<{rows: Array|null, changed: boolean}>} Active result.
   */
  const refresh = useCallback((options = {}) => {
    if (inFlightRef.current && !options.completed && !options.force) return inFlightRef.current;
    refreshSeqRef.current += 1;
    const seq = refreshSeqRef.current;
    const run = (async () => {
      const result = await fetchActiveRequests(seq);
      const rows = result?.rows;
      let finishedSome = false;
      if (rows) {
        const ids = new Set(rows.map((r) => r.id));
        activeIdsRef.current.forEach((id) => {
          if (!ids.has(id)) finishedSome = true;
        });
        activeIdsRef.current = ids;
      }
      if (options.completed || finishedSome) await fetchCompletedRequests(seq);
      return result || { rows: null, changed: false };
    })();
    inFlightRef.current = run;
    run.finally(() => {
      if (inFlightRef.current === run) inFlightRef.current = null;
    });
    return run;
  }, [fetchActiveRequests, fetchCompletedRequests]);

  /**
   * (Re)schedule the next poll. `immediate` runs a fresh refresh now (never an older in-flight
   * one, which could predate the write that triggered it) and resets the backoff.
   * @param {{immediate?: boolean, completed?: boolean}} [options] - Options.
   * @returns {Promise<{rows: Array|null, changed: boolean}|null>} The immediate refresh result.
   */
  const schedule = useCallback(async ({ immediate = false, completed = false } = {}) => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
    if (!userIdRef.current) return null;
    let result = null;
    if (immediate) {
      delayRef.current = 0;
      result = await refresh({ completed, force: true });
      if (!userIdRef.current) return result;
      delayRef.current = nextPollDelay({ activeCount: result.rows?.length ?? activeIdsRef.current.size, changed: true, previousDelay: 0 });
    }
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return result;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      timerRef.current = null;
      const activeCount = activeIdsRef.current.size;
      let completedToo = false;
      if (!activeCount) {
        idleTicksRef.current += 1;
        completedToo = idleTicksRef.current % IDLE_COMPLETED_EVERY === 0;
      } else {
        idleTicksRef.current = 0;
      }
      const result = await refresh({ completed: completedToo });
      // Signed out, or an immediate refresh (create/cancel/realtime/visibility) already set the
      // next poll while this one was in flight: keep that schedule.
      if (!userIdRef.current || timerRef.current) return;
      delayRef.current = nextPollDelay({
        activeCount: result.rows?.length ?? activeIdsRef.current.size,
        changed: result.changed,
        previousDelay: delayRef.current,
      });
      schedule();
    }, delayRef.current || ACTIVE_POLL_MS);
    return result;
  }, [refresh]);

  // Sign-in / sign-out: reset, load both lists, start the scheduler.
  useEffect(() => {
    clearTimeout(timerRef.current);
    activeSigRef.current = '';
    completedSigRef.current = '';
    activeIdsRef.current = new Set();
    delayRef.current = 0;
    idleTicksRef.current = 0;
    // Results of refreshes started for the previous user are stale from here on.
    appliedActiveSeqRef.current = refreshSeqRef.current + 1;
    appliedCompletedSeqRef.current = refreshSeqRef.current + 1;
    setActiveRequests([]);
    setCompletedRequests([]);
    setCompletedLoadedFor(null);
    if (!userId) return undefined;
    schedule({ immediate: true, completed: true });
    return () => clearTimeout(timerRef.current);
  }, [userId, schedule]);

  // Pause while hidden; catch up as soon as the tab is visible again.
  useEffect(() => {
    if (!userId) return undefined;
    const onVisibility = () => {
      if (document.visibilityState === 'visible') schedule({ immediate: true, completed: true });
      else clearTimeout(timerRef.current);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [userId, schedule]);

  // Single realtime channel for this user's rows; events trigger one coalesced refresh.
  useEffect(() => {
    if (!userId) return undefined;
    let debounce = null;
    let wantsCompleted = false;
    const channel = supabase
      .channel(`ai_requests:${userId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'ai_requests', filter: `user_id=eq.${userId}` },
        (payload) => {
          const status = payload.new?.status;
          if (status && !ACTIVE_STATUSES.includes(status)) wantsCompleted = true;
          clearTimeout(debounce);
          debounce = setTimeout(() => {
            const completed = wantsCompleted;
            wantsCompleted = false;
            schedule({ immediate: true, completed });
          }, REALTIME_DEBOUNCE_MS);
        }
      )
      .subscribe();
    return () => {
      clearTimeout(debounce);
      supabase.removeChannel(channel);
    };
  }, [userId, schedule]);

  // Create a new AI request
  const createRequest = useCallback(async (requestData) => {
    if (!userIdRef.current) throw new Error('User not authenticated');
    try {
      const { data, error } = await supabase
        .from('ai_requests')
        .insert([{
          user_id: userIdRef.current,
          type: requestData.type || 'document',
          status: 'pending',
          title: requestData.title,
          project_id: requestData.projectId,
          template_id: requestData.templateId,
          request_data: requestData.data || {},
          priority: requestData.priority || 0,
          dismissed: false,
        }])
        .select()
        .single();
      if (error) throw error;
      await schedule({ immediate: true });
      return data;
    } catch (error) {
      console.error('Error creating AI request:', error);
      throw error;
    }
  }, [schedule]);

  // Dismiss a completed request
  const dismissRequest = useCallback(async (requestId) => {
    try {
      const { error } = await supabase
        .from('ai_requests')
        .update({ dismissed: true })
        .eq('id', requestId);
      if (error) throw error;
      // Remove locally right away (keep signatures in sync so the next poll is a no-op).
      setCompletedRequests((prev) => {
        const next = prev.filter((req) => req.id !== requestId);
        completedSigRef.current = requestsSignature(next);
        return next;
      });
      setActiveRequests((prev) => {
        const next = prev.filter((req) => req.id !== requestId);
        activeSigRef.current = requestsSignature(next);
        return next;
      });
    } catch (error) {
      console.error('Error dismissing request:', error);
    }
  }, []);

  // Cancel a pending/processing request
  const cancelRequest = useCallback(async (requestId) => {
    try {
      const { error } = await supabase
        .from('ai_requests')
        .update({
          status: 'cancelled',
          completed_at: new Date().toISOString(),
        })
        .eq('id', requestId);
      if (error) throw error;
      await schedule({ immediate: true });
    } catch (error) {
      console.error('Error cancelling request:', error);
      throw error;
    }
  }, [schedule]);

  /**
   * Refresh now and poll quickly. Call after dispatching a server-side task so the UI picks
   * it up even when realtime is unavailable.
   * @returns {Promise<Array>} Active requests.
   */
  const refreshRequests = useCallback(async () => {
    const result = await schedule({ immediate: true, completed: true });
    return result?.rows || [];
  }, [schedule]);

  const completedLoaded = Boolean(userId) && completedLoadedFor === userId;

  const value = useMemo(() => ({
    activeRequests,
    completedRequests,
    completedLoaded,
    loading: false,
    createRequest,
    dismissRequest,
    cancelRequest,
    refreshRequests,
  }), [activeRequests, completedRequests, completedLoaded, createRequest, dismissRequest, cancelRequest, refreshRequests]);

  return (
    <AIRequestContext.Provider value={value}>
      {children}
    </AIRequestContext.Provider>
  );
};

/**
 * Always-mounted watcher: when an AI job finishes while the app is open, reload projects so
 * its output (documents, requirements, insights) appears in every list. Jobs already finished
 * at sign-in are history, not news: the baseline is taken once the provider reports that the
 * first completed-jobs fetch resolved. Lives outside the top bar so focus mode (which hides
 * the top bar) does not miss completions.
 * @param {{onJobsCompleted: (rows: Array<object>) => void}} props - Called with the newly finished ai_requests rows.
 * @returns {null} Renders nothing.
 */
export function AIJobCompletionWatcher({ onJobsCompleted }) {
  const { completedRequests, completedLoaded } = useAIRequests();
  /** Ids already seen; null until the sign-in baseline is taken. */
  const seenRef = useRef(null);
  const callbackRef = useRef(onJobsCompleted);
  callbackRef.current = onJobsCompleted;

  useEffect(() => {
    if (!completedLoaded) {
      seenRef.current = null;
      return;
    }
    if (!seenRef.current) {
      seenRef.current = new Set(completedRequests.map((r) => r.id));
      return;
    }
    const freshIds = new Set(newRequestIds(completedRequests, seenRef.current));
    if (!freshIds.size) return;
    // Keep earlier ids too: a job that drops out of the list and comes back is not new.
    freshIds.forEach((id) => seenRef.current.add(id));
    callbackRef.current?.(completedRequests.filter((r) => freshIds.has(r.id)));
  }, [completedRequests, completedLoaded]);

  return null;
}
